import { useMemo, useState } from "react";
import { Avatar, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";

import {
  account,
  applyPromo,
  cancel,
  changePassword,
  contactsLabel,
  fiatCurrency,
  profile,
  security,
  settings,
  signingOut,
  signOut,
  signOutConfirmationMsg,
  twoStepVerification,
} from "../messages";
import "../../assets/settings.scss";
import SettingsMenuComponent from "../SettingsMenuComponent";
import { logOut, planTagsColor, profileSectionStyles } from "../constants";
import { useSession } from "../../hooks/useSession";
import CommonHeader from "../CommonHeader";
import Text from "../text/Text";
import { Modal } from "../Modal/Modal";
import Button from "../button/Button";
import CustomDivider from "../divider/Divider";
import {
  accountIcon,
  applyPromocodeIcon,
  changePasswordIcon,
  contactIcon,
  copyIcon,
  currencyIcon,
  limitIcon,
  logoutIcon,
  logoutIconBlue,
  profileIcon,
  speedLogo,
  twoStepVerificationIcon,
} from "../images";
import { CustomTooltip } from "../tooltip/Tooltip";
import { Tag } from "../tag/Tag";
import CommonDrawer from "../drawer/CommonDrawer";

const renderModalContent = () => {
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <img src={logoutIconBlue} height={44} width={44} alt="logout icon" />
      <Text className="default-text mt-8" size={20} font="bold">
        {signingOut}
      </Text>
      <Text className="grey-text" size={14} font="regular">
        {signOutConfirmationMsg}
      </Text>
    </Box>
  );
};

function Settings() {
  const navigate = useNavigate();
  const { session } = useSession();
  const { walletCurrency } = useSelector((state) => state.common);

  const [disabledLogout, setDisabledLogout] = useState(false);
  const [isLogoutConfirm, setIsLogoutConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const user = session?.user;
  const accountDetails = session?.account;
  const plan = session?.account?.account?.plan?.plan_name || "Base";

  let initials =
    user?.first_name.charAt(0) + (user?.last_name?.charAt(0) || "");
  initials = initials.toUpperCase();

  const handleLogout = async () => {
    setDisabledLogout(true);
    try {
      await logOut();
    } catch (error) {
    } finally {
      setDisabledLogout(false);
    }
  };

  const copyToClipboard = () => {
    navigator?.clipboard
      ?.writeText(accountDetails?.account?.id)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      })
      .catch(() => {});
  };

  const settingsArr = useMemo(
    () => [
      {
        section: "Display",
        menus: [
          {
            name: fiatCurrency,
            icon: (
              <img
                src={currencyIcon}
                height={24}
                width={24}
                alt="currency icon"
              />
            ),
            text: walletCurrency?.code,
            action: () => navigate("/settings/fiat-currency"),
          },
        ],
      },
      {
        section: "General ",
        menus: [
          {
            name: profile,
            icon: (
              <img
                src={profileIcon}
                height={24}
                width={24}
                alt="profile icon"
              />
            ),
            text: null,
            action: () => navigate("/profile"),
          },
          {
            name: account,
            icon: (
              <img
                src={accountIcon}
                height={24}
                width={24}
                alt="account icon"
              />
            ),
            text: null,
            action: () => navigate("/settings/advance-settings"),
          },
          {
            name: contactsLabel,
            icon: (
              <img
                src={contactIcon}
                height={24}
                width={24}
                alt="contact icon"
              />
            ),
            text: null,
            action: () => navigate("/settings/contacts"),
          },
          {
            name: "Limits",
            icon: (
              <img src={limitIcon} height={24} width={24} alt="limit icon" />
            ),
            text: null,
            action: () => navigate("/settings/limits"),
          },
        ],
      },
      {
        section: "Privacy & security ",
        menus: [
          ...(user?.signup_method === "email"
            ? [
                {
                  name: changePassword,
                  icon: (
                    <img
                      src={changePasswordIcon}
                      height={24}
                      width={24}
                      alt="change password icon"
                    />
                  ),
                  text: null,
                  action: () => navigate("/settings/security/change-password"),
                },
              ]
            : []),
          {
            name: twoStepVerification,
            icon: (
              <img
                src={twoStepVerificationIcon}
                height={24}
                width={24}
                alt="two step verification icon"
              />
            ),
            text: null,
            action: () => navigate("/settings/two-step-verification"),
          },
        ],
      },
      {
        section: "More",
        menus: [
          {
            name: applyPromo,
            icon: (
              <img
                src={applyPromocodeIcon}
                height={24}
                width={24}
                alt="apply promo icon"
              />
            ),
            text: null,
            action: () => navigate("/settings/apply-promo-code"),
          },
          {
            name: signOut,
            icon: (
              <img src={logoutIcon} height={24} width={24} alt="logout icon" />
            ),
            text: null,
            action: () => setIsLogoutConfirm(true),
          },
        ],
      },
    ],
    [navigate, user?.signup_method, walletCurrency]
  );

  const renderModalFooter = () => {
    return (
      <Box display="flex" justifyContent="space-between" gap="15px">
        <Button
          fullWidth
          variant="outlined"
          label={cancel}
          onClick={() => setIsLogoutConfirm(false)}
          disabled={disabledLogout}
        />
        <Button
          fullWidth
          variant="contained"
          label={signOut}
          onClick={handleLogout}
          loading={disabledLogout}
          disabled={disabledLogout}
        />
      </Box>
    );
  };

  return (
    <>
      <CommonHeader textAfterIcon={settings} />
      <Box className="options-container">
        <Box className="settings-wrapper">
          <Box className="module-container">
            <Box
              className="user-details-container"
              onClick={() => navigate("/profile")}
              sx={{
                backgroundColor: profileSectionStyles[plan]?.boxBackgroundColor,
                backgroundImage: profileSectionStyles[plan]?.boxBackgroundImage,
              }}
            >
              <Avatar
                className="user-avatar"
                sx={{
                  background: profileSectionStyles[plan]?.avatarBackground,
                  border: profileSectionStyles[plan]?.avatarBorder,
                }}
              >
                <Text
                  sx={{ color: profileSectionStyles[plan]?.avatarTextColor }}
                  size={14}
                  font="semibold"
                >
                  {initials}
                </Text>
              </Avatar>
              <Box className="user-data">
                <Box className="user-info">
                  <Box display="flex" flexDirection="column">
                    <Text
                      size={16}
                      className="userName"
                      font="semibold"
                      sx={{
                        color: profileSectionStyles[plan]?.textColor,
                      }}
                    >
                      {user?.first_name + " " + user?.last_name}
                    </Text>
                    <Text
                      size={14}
                      className="userEmail"
                      font="regular"
                      sx={{
                        color: profileSectionStyles[plan]?.textColor,
                      }}
                    >
                      {user?.email}
                    </Text>
                  </Box>
                  <KeyboardArrowRightOutlinedIcon
                    sx={{
                      height: "24px",
                      width: "24px",
                      color: "#848B9E",
                      cursor: "pointer",
                    }}
                  />
                </Box>

                <CustomDivider
                  sx={{
                    width: "100%",
                    opacity: `${plan === "Premium" ? 0.1 : 1}`,
                    borderColor: "#F0F0F0",
                    borderWidth: "1px",
                    margin: "12px 0px",
                  }}
                />
                <Tag
                  text={plan}
                  style={{
                    borderRadius: "30px",
                    background: planTagsColor[plan],
                  }}
                />
              </Box>
            </Box>
            <SettingsMenuComponent
              menu={
                session?.user?.signup_method !== "email"
                  ? settingsArr.filter((setting) => setting.name !== security)
                  : settingsArr
              }
            />
            <Box className="copy-address-container">
              <CustomTooltip
                arrow={false}
                placement="top"
                text={
                  <>
                    <CheckCircleSharpIcon />
                    <span>Copied</span>{" "}
                  </>
                }
                open={copied}
              >
                <Box className="copy-address">
                  <Text size={16} font="medium">
                    {accountDetails?.account?.id}
                  </Text>
                  <img
                    src={copyIcon}
                    height={24}
                    width={24}
                    alt="copy icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard();
                    }}
                  />
                </Box>
              </CustomTooltip>
            </Box>
            <Box display="flex" justifyContent="center">
              <img
                src={speedLogo}
                height={40}
                width={100}
                alt="speed footer logo"
              />
            </Box>
          </Box>
          {isLogoutConfirm && (
            <CommonDrawer
              anchor="bottom"
              open={isLogoutConfirm}
              onClose={() => setIsLogoutConfirm(false)}
              className="logout-drawer"
            >
              <Box className="logout-drawer-container">
                {renderModalContent()}
              </Box>
              <Box className="logout-drawer-actions">{renderModalFooter()}</Box>
            </CommonDrawer>
          )}
        </Box>
      </Box>
    </>
  );
}

export default Settings;
